import gsap from 'gsap';

/** Composante FloatingShapes de TimTools */
export default class FloatingShapes {
  constructor(element) {
    this.element = element;
    this.svg = element.matches('svg')
      ? element
      : element.querySelector('.meet__background');
    this.container = element.matches('svg') ? element.parentElement : element;
    this.floaters = this.svg
      ? [...this.svg.querySelectorAll('.meet__floater')]
      : [];
    this.options = {
      count: 12,
      spreadX: 0.8,
      spreadY: 0.2,
      minSize: 0.1,
      maxSize: 1,
      drift: 0.4,
      speed: 0.8,
    };
    this.items = [];
    this.clones = [];
    this.time = 0;
    this.isVisible = false;
    this.isRunning = false;
    this.motionPreference = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );
    this.onTick = this.update.bind(this);
    this.onResize = this.resize.bind(this);
    this.onMotionChange = this.syncAnimation.bind(this);
    this.init();
  }

  init() {
    if (!this.svg || !this.floaters.length) return;
    this.originalViewBox = this.svg.getAttribute('viewBox');
    this.createShapes();

    this.context = gsap.context(() => {
      this.floaters.forEach((floater) => {
        const shape = floater.querySelector('.meet__shape');
        if (!shape) return;
        const bounds = floater.getBBox();
        if (!bounds.width || !bounds.height) return;
        const position = this.randomPosition();
        const rotation = gsap.utils.random(-12, 12);
        gsap.set(floater, { x: 0, y: 0 });
        gsap.set(shape, {
          rotation,
          scale: 1,
          transformOrigin: 'center center',
        });

        this.items.push({
          centerX: bounds.x + bounds.width / 2,
          centerY: bounds.y + bounds.height / 2,
          width: bounds.width,
          height: bounds.height,
          position,
          sizeRatio: gsap.utils.random(
            this.options.minSize,
            this.options.maxSize,
          ),
          driftRatio: gsap.utils.random(0.5, 1),
          rotation,
          phaseX: Math.random() * Math.PI * 2,
          phaseY: Math.random() * Math.PI * 2,
          frequency: gsap.utils.random(0.7, 1.1),
          setX: gsap.quickSetter(floater, 'x', 'px'),
          setY: gsap.quickSetter(floater, 'y', 'px'),
          setRotation: gsap.quickSetter(shape, 'rotation', 'deg'),
          setScale: gsap.quickSetter(shape, 'scale'),
        });
      });
    }, this.element);

    this.resize();
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.container);
    this.observer = new IntersectionObserver((entries) => {
      this.isVisible = entries.some((entry) => entry.isIntersecting);
      this.syncAnimation();
    });
    this.observer.observe(this.container);
    this.motionPreference.addEventListener('change', this.onMotionChange);
  }

  createShapes() {
    const templates = [...this.floaters];
    while (this.floaters.length < this.options.count) {
      const template = templates[Math.floor(Math.random() * templates.length)];
      const clone = template.cloneNode(true);
      template.parentElement.append(clone);
      this.clones.push(clone);
      this.floaters.push(clone);
    }
  }

  randomPosition() {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.sqrt(Math.random());
    return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  }

  resize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (!width || !height) return;

    // Une unité SVG correspond à un pixel de la section, indépendamment du viewBox initial.
    this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    for (const item of this.items) {
      const size = Math.min(height * item.sizeRatio, width * 0.3);
      item.scale = size / Math.max(item.width, item.height);
      // Réserve la place de la forme tournée, de sa respiration et du filtre goo.
      const margin =
        (Math.hypot(item.width, item.height) * item.scale * 1.025) / 2 + 16;
      const availableX = Math.max(0, width / 2 - margin);
      const availableY = Math.max(0, height / 2 - margin);
      item.amplitude = Math.min(
        height * this.options.drift * item.driftRatio,
        availableX,
        availableY,
      );
      const radiusX = Math.min(
        height * this.options.spreadX,
        availableX - item.amplitude,
      );
      const radiusY = Math.min(
        height * this.options.spreadY,
        availableY - item.amplitude,
      );
      item.x = width / 2 + item.position.x * radiusX - item.centerX;
      item.y = height / 2 + item.position.y * radiusY - item.centerY;
    }
    this.update(0, 0);
  }

  syncAnimation() {
    const shouldRun = this.isVisible && !this.motionPreference.matches;
    if (shouldRun === this.isRunning) return;
    this.isRunning = shouldRun;
    if (shouldRun) {
      gsap.ticker.add(this.onTick);
    } else {
      gsap.ticker.remove(this.onTick);
    }
  }

  update(time, deltaTime) {
    // Une pause de l'onglet ne doit pas provoquer de saut à la reprise.
    this.time += (Math.min(deltaTime, 64) / 1000) * this.options.speed;
    for (const item of this.items) {
      if (item.scale === undefined) continue;
      const t = this.time * item.frequency;
      const dx =
        (Math.sin(t + item.phaseX) + 0.35 * Math.sin(t * 0.63 + item.phaseY)) /
        1.35;
      const dy =
        (Math.sin(t * 0.81 + item.phaseY) +
          0.35 * Math.sin(t * 0.47 + item.phaseX)) /
        1.35;
      item.setX(item.x + dx * item.amplitude);
      item.setY(item.y + dy * item.amplitude * 0.7);
      item.setRotation(item.rotation + Math.sin(t * 0.55 + item.phaseX) * 6);
      item.setScale(item.scale * (1 + Math.sin(t * 0.7 + item.phaseY) * 0.025));
    }
  }

  destroy() {
    gsap.ticker.remove(this.onTick);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.motionPreference.removeEventListener('change', this.onMotionChange);
    this.context?.revert();
    this.clones.forEach((clone) => clone.remove());
    if (this.originalViewBox !== undefined) {
      if (this.originalViewBox === null) this.svg.removeAttribute('viewBox');
      else this.svg.setAttribute('viewBox', this.originalViewBox);
    }
    this.clones = [];
    this.items = [];
    this.isRunning = false;
  }
}
